import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import PropertyCard from '@/components/PropertyCard';
import { getProperties, getPropertyCount, toggleSaveProperty, getSavedProperties, getCities, createSavedSearch } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { SlidersHorizontal, Building2, X, Grid3X3, List, Map, Search, Bell } from 'lucide-react';
import Seo from '@/components/Seo';

const PAGE_SIZE = 24;
const FILTER_KEYS = ['listing_type', 'property_type', 'city', 'locality', 'q', 'min_price', 'max_price', 'bedrooms', 'furnishing', 'is_project'];
const LABELS = { listing_type: 'For', property_type: 'Type', city: 'City', locality: 'Area', q: 'Search', min_price: 'Min', max_price: 'Max', bedrooms: 'BHK', furnishing: 'Furnishing', is_project: 'Projects' };
const Chip = ({ on, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid} className={`px-3.5 py-2 text-sm font-medium rounded-full border transition-colors ${on ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-slate-700 border-slate-200 hover:border-[#0F172A]'}`}>{children}</button>
);

const PropertiesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [savedIds, setSavedIds] = useState(new Set());
  const [cities, setCities] = useState([]);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('be_view') || 'grid');
  const { user } = useAuth();

  const handleSaveSearch = async () => {
    if (!user) return toast.error('Please sign in to save searches and get alerts');
    const filters = {};
    FILTER_KEYS.forEach((k) => { const v = searchParams.get(k); if (v) filters[k] = v; });
    const nameParts = [filters.listing_type === 'rent' ? 'Rent' : filters.listing_type ? 'Buy' : '', filters.bedrooms ? `${filters.bedrooms}BHK` : '', filters.property_type || '', filters.city || filters.locality || 'India'].filter(Boolean);
    const name = nameParts.join(' ') || 'My search';
    try { await createSavedSearch({ name, filters, alerts_enabled: true }); toast.success('Search saved — we\'ll email you when new matches are posted'); }
    catch (e) { toast.error(e.message || 'Could not save search'); }
  };

  const applied = useMemo(() => Object.fromEntries(FILTER_KEYS.map((k) => [k, searchParams.get(k) || ''])), [searchParams]);
  const [draft, setDraft] = useState(applied);
  useEffect(() => { setDraft(applied); }, [applied]);

  const apiFilters = useMemo(() => ({ ...Object.fromEntries(Object.entries(applied).filter(([, v]) => v)), sort: searchParams.get('sort') || undefined }), [applied, searchParams]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([getProperties({ ...apiFilters, skip: 0, limit: PAGE_SIZE }), getPropertyCount(apiFilters), getCities()])
      .then(([data, count, c]) => { if (cancelled) return; setProperties(data); setTotal(count.count); setCities(c.cities || []); })
      .catch(() => toast.error('Failed to load properties'))
      .finally(() => !cancelled && setLoading(false));
    if (user) getSavedProperties().then((s) => setSavedIds(new Set(s.map((p) => p.property_id)))).catch(() => {});
    return () => { cancelled = true; };
  }, [apiFilters, user]);

  const loadMore = async () => {
    setLoadingMore(true);
    try { const next = await getProperties({ ...apiFilters, skip: properties.length, limit: PAGE_SIZE }); setProperties((p) => [...p, ...next]); }
    catch { toast.error('Failed to load more'); } finally { setLoadingMore(false); }
  };

  const commit = (next) => {
    const p = new URLSearchParams();
    Object.entries(next).forEach(([k, v]) => v && p.set(k, v));
    if (searchParams.get('sort')) p.set('sort', searchParams.get('sort'));
    setSearchParams(p);
  };
  const setD = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const clearAll = () => { setDraft(Object.fromEntries(FILTER_KEYS.map((k) => [k, '']))); setSearchParams(new URLSearchParams()); };
  const activeCount = Object.values(applied).filter(Boolean).length;
  const setView = (v) => { setViewMode(v); localStorage.setItem('be_view', v); };

  const handleSave = async (id) => {
    if (!user) return toast.error('Please login to save properties');
    try { const r = await toggleSaveProperty(id); setSavedIds((prev) => { const n = new Set(prev); r.saved ? n.add(id) : n.delete(id); return n; }); toast.success(r.saved ? 'Property saved' : 'Removed from saved'); }
    catch { toast.error('Failed to save property'); }
  };

  const title = applied.is_project ? 'New Projects' : applied.property_type === 'commercial' ? 'Commercial Properties' : applied.listing_type === 'rent' ? 'Properties for Rent' : applied.listing_type === 'buy' ? 'Properties for Sale' : 'All Properties';
  const where = applied.locality ? `${applied.locality}, ${applied.city || ''}`.replace(/, $/, '') : applied.city || (applied.q ? `“${applied.q}”` : 'India');

  const FilterPanel = (
    <div className="space-y-6" data-testid="filter-panel">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2.5">Looking to</p>
        <div className="flex flex-wrap gap-2">{['buy', 'rent'].map((t) => <Chip key={t} on={draft.listing_type === t} onClick={() => setD('listing_type', draft.listing_type === t ? '' : t)} testid={`filter-listing-${t}`}>{t === 'buy' ? 'Buy' : 'Rent'}</Chip>)}<Chip on={draft.is_project === 'true'} onClick={() => setD('is_project', draft.is_project ? '' : 'true')} testid="filter-projects">New Projects</Chip></div>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2.5">Property type</p>
        <div className="flex flex-wrap gap-2">{[['apartment', 'Apartment'], ['house', 'House'], ['villa', 'Villa'], ['plot', 'Plot'], ['commercial', 'Commercial']].map(([v, l]) => <Chip key={v} on={draft.property_type === v} onClick={() => setD('property_type', draft.property_type === v ? '' : v)} testid={`filter-type-${v}`}>{l}</Chip>)}</div>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2.5">City</p>
        <Select value={draft.city || 'all'} onValueChange={(v) => setD('city', v === 'all' ? '' : v)}>
          <SelectTrigger className="rounded-xl" data-testid="filter-city"><SelectValue placeholder="All cities" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All cities</SelectItem>{cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2.5">Bedrooms</p>
        <div className="flex flex-wrap gap-2">{['1', '2', '3', '4', '5'].map((n) => <Chip key={n} on={draft.bedrooms === n} onClick={() => setD('bedrooms', draft.bedrooms === n ? '' : n)} testid={`filter-bedroom-${n}`}>{n}{n === '5' ? '+' : ''} BHK</Chip>)}</div>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2.5">Budget (₹)</p>
        <div className="grid grid-cols-2 gap-3">
          <Input type="number" placeholder="Min" value={draft.min_price} onChange={(e) => setD('min_price', e.target.value)} className="h-10 rounded-xl" data-testid="filter-min-price" />
          <Input type="number" placeholder="Max" value={draft.max_price} onChange={(e) => setD('max_price', e.target.value)} className="h-10 rounded-xl" data-testid="filter-max-price" />
        </div>
        <p className="text-[11px] text-slate-500 mt-2">e.g. 5000000 = ₹50 Lac · 10000000 = ₹1 Cr. Rentals: monthly figures.</p>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2.5">Furnishing</p>
        <Select value={draft.furnishing || 'any'} onValueChange={(v) => setD('furnishing', v === 'any' ? '' : v)}>
          <SelectTrigger className="rounded-xl" data-testid="filter-furnishing"><SelectValue placeholder="Any" /></SelectTrigger>
          <SelectContent><SelectItem value="any">Any</SelectItem><SelectItem value="furnished">Furnished</SelectItem><SelectItem value="semi-furnished">Semi-furnished</SelectItem><SelectItem value="unfurnished">Unfurnished</SelectItem></SelectContent>
        </Select>
      </div>
      <div className="flex gap-3 pt-4 border-t border-slate-200">
        <Button onClick={clearAll} variant="outline" className="flex-1 rounded-full" data-testid="clear-filters-btn">Clear</Button>
        <Button onClick={() => commit(draft)} className="flex-1 rounded-full bg-[#0F172A] text-white hover:bg-[#1E293B]" data-testid="apply-filters-btn">Apply</Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-16">
      <Seo title={`${title} in ${where}`} description="Browse verified properties for sale and rent across India. Filter by city, type, budget, and bedrooms." path="/properties" />

      <div className="bg-white border-b border-slate-200">
        <div className="container-luxury py-4 flex flex-col md:flex-row md:items-center gap-4">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A] truncate" data-testid="results-title">{title} <span className="text-slate-400 font-medium">in</span> {where}</h1>
            <p className="text-sm text-slate-500" data-testid="results-count">{total === null ? '…' : `${total.toLocaleString('en-IN')} propert${total === 1 ? 'y' : 'ies'}`}{activeCount ? ` · ${activeCount} filter${activeCount > 1 ? 's' : ''}` : ''}</p>
          </div>
          <div className="md:ml-auto flex items-center gap-2 flex-wrap">
            <Sheet>
              <SheetTrigger asChild><Button variant="outline" className="lg:hidden rounded-full h-10" data-testid="mobile-filter-btn"><SlidersHorizontal className="h-4 w-4 mr-2" /> Filters{activeCount ? ` (${activeCount})` : ''}</Button></SheetTrigger>
              <SheetContent side="left" className="w-[90vw] sm:w-96 overflow-y-auto"><SheetHeader><SheetTitle>Filters</SheetTitle></SheetHeader><div className="mt-6">{FilterPanel}</div></SheetContent>
            </Sheet>
            <Button onClick={handleSaveSearch} variant="outline" className="rounded-full h-10 border-[#D4AF37] text-[#B5952F] hover:bg-[#FBF7E9]" data-testid="save-search-btn"><Bell className="h-4 w-4 mr-2" /> Save this search</Button>
            <Select value={searchParams.get('sort') || 'newest'} onValueChange={(v) => { const p = new URLSearchParams(searchParams); v === 'newest' ? p.delete('sort') : p.set('sort', v); setSearchParams(p); }}>
              <SelectTrigger className="w-[170px] h-10 rounded-full" data-testid="sort-select"><SelectValue placeholder="Sort by" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="newest" data-testid="sort-newest">Newest first</SelectItem>
                <SelectItem value="price_low" data-testid="sort-price-low">Price: Low to High</SelectItem>
                <SelectItem value="price_high" data-testid="sort-price-high">Price: High to Low</SelectItem>
                <SelectItem value="views" data-testid="sort-views">Most viewed</SelectItem>
              </SelectContent>
            </Select>
            <div className="inline-flex items-center rounded-full border border-slate-200 bg-white p-1" data-testid="view-toggle">
              <button onClick={() => setView('list')} className={`h-8 px-3 rounded-full text-sm font-medium inline-flex items-center gap-1.5 ${viewMode === 'list' ? 'bg-[#0F172A] text-white' : 'text-slate-600 hover:bg-slate-100'}`} data-testid="view-list"><List className="h-4 w-4" /> List</button>
              <button onClick={() => setView('grid')} className={`h-8 px-3 rounded-full text-sm font-medium inline-flex items-center gap-1.5 ${viewMode === 'grid' ? 'bg-[#0F172A] text-white' : 'text-slate-600 hover:bg-slate-100'}`} data-testid="view-grid"><Grid3X3 className="h-4 w-4" /> Grid</button>
              <Link to={`/map-search?${searchParams.toString()}`} className="h-8 px-3 rounded-full text-sm font-medium inline-flex items-center gap-1.5 text-slate-600 hover:bg-slate-100" data-testid="view-map"><Map className="h-4 w-4" /> Map</Link>
            </div>
          </div>
        </div>
      </div>

      <div className="container-luxury pt-6">
        <div className="flex gap-8">
          <aside className="hidden lg:block w-72 flex-shrink-0">
            <div className="bg-white p-6 rounded-2xl border border-slate-200 sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto">
              <div className="flex items-center justify-between mb-5"><h2 className="font-bold text-[#0F172A]">Filters</h2>{activeCount > 0 && <button onClick={clearAll} className="text-sm text-slate-500 hover:text-[#0F172A]" data-testid="clear-all-btn">Clear all</button>}</div>
              {FilterPanel}
            </div>
          </aside>

          <main className="flex-1 min-w-0">
            {activeCount > 0 && (
              <div className="flex flex-wrap gap-2 mb-5" data-testid="active-filters">
                {Object.entries(applied).filter(([, v]) => v).map(([k, v]) => (
                  <span key={k} className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1 bg-white border border-slate-200 text-[#0F172A] text-sm rounded-full">
                    <span className="text-slate-500">{LABELS[k]}:</span> {k === 'is_project' ? 'Yes' : v}
                    <button onClick={() => commit({ ...applied, [k]: '' })} className="h-5 w-5 rounded-full hover:bg-slate-100 inline-flex items-center justify-center" data-testid={`remove-filter-${k}`}><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            )}

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">{[...Array(6)].map((_, i) => <div key={i} className="bg-white rounded-2xl border border-slate-200 animate-pulse overflow-hidden"><div className="aspect-[4/3] bg-slate-200" /><div className="p-4 space-y-3"><div className="h-6 bg-slate-200 rounded w-1/2" /><div className="h-4 bg-slate-200 rounded w-3/4" /><div className="h-4 bg-slate-200 rounded w-full" /></div></div>)}</div>
            ) : properties.length > 0 ? (
              <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6' : 'space-y-5'}>
                {properties.map((p) => <PropertyCard key={p.property_id} property={p} onSave={handleSave} isSaved={savedIds.has(p.property_id)} variant={viewMode} />)}
              </div>
            ) : (
              <div className="text-center py-24 bg-white rounded-2xl border border-slate-200" data-testid="no-results">
                <Building2 className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-bold text-[#0F172A] mb-1">No properties match these filters</h3>
                <p className="text-slate-500 mb-6">Try widening your budget or removing a filter.</p>
                <div className="flex justify-center gap-3"><Button onClick={clearAll} variant="outline" className="rounded-full" data-testid="no-results-clear-btn">Clear filters</Button><Link to="/map-search"><Button className="rounded-full bg-[#0F172A] text-white hover:bg-[#1E293B]"><Search className="h-4 w-4 mr-2" /> Explore on map</Button></Link></div>
              </div>
            )}

            {!loading && total !== null && properties.length < total && (
              <div className="flex flex-col items-center mt-10 gap-2">
                <p className="text-sm text-slate-500">Showing {properties.length} of {total.toLocaleString('en-IN')}</p>
                <Button onClick={loadMore} disabled={loadingMore} variant="outline" className="rounded-full px-10 h-12 border-slate-300 hover:border-[#0F172A]" data-testid="load-more-btn">{loadingMore ? 'Loading…' : 'Show more properties'}</Button>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default PropertiesPage;
